import React from 'react';
import * as PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { concat, range, slice } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

export function _maxNumberOfPages(total, perPage) {
  return Math.ceil(total / perPage);
}

/**
 * @param {number} total
 * @param {number} perPage
 * @param {number} pageNumber
 * @return {number} page number
 */
export function _handlePage(total, perPage, pageNumber) {
  const max = _maxNumberOfPages(total, perPage);
  if (pageNumber >= max) return max;
  if (pageNumber <= 1) return 1;
  return pageNumber;
}

function _paginationItem(t, link, page, index, current = false, sortingValue = null) {
  if (isNaN(page)) return null;

  return (
    <li className="page-item" key={index} data-e2e={`paginationItem${current ? 'Current' : ''}`}>
      <Link
        className={`page-link ${current ? 'current' : ''}`}
        to={`${link.replace('#page#', page)}`}
        aria-label={t('pagination.page.label', { page })}
        aria-current={current ? 'page' : undefined}
      >
        {page}
      </Link>
    </li>
  );
}

const _paginationSeparator = (key) => (
  <li className="page-item tripledots" key={key}>
    ...
  </li>
);

export function _injectFirstPage(t, jsx, link, pageNumber, sortingValue) {
  return pageNumber > 1
    ? concat(
        _paginationItem(t, link, 1, 936583627126347464, pageNumber === 1, sortingValue),
        _paginationSeparator(658398734759872394),
        jsx
      )
    : jsx;
}

export function lessThen5ItemsView(t, max, link, pageNumber, sortingValue) {
  return range(1, max + 1).map((page, index) => {
    return _paginationItem(t, link, page, index, pageNumber === page, sortingValue);
  });
}

/** < 1 2 3 ... 99 > */
export function splittedViewLeft(t, max, link, pageNumber, sortingValue) {
  const linksJsx = range(1, 4).map((page, index) =>
    _paginationItem(t, link, page, index, pageNumber === page, sortingValue)
  );
  return concat(
    linksJsx,
    _paginationSeparator(562937649347654874),
    _paginationItem(t, link, max, max - 1, pageNumber === max, sortingValue)
  );
}

/**
 * < 1 ... 2 3 4 ... 99 >
 * < 1 ... 32 33 34 ... 99 >
 * < 1 ... 96 97 98 ... 99 >
 */
export function splittedViewMiddle(t, max, link, pageNumber, sortingValue) {
  const linksJsx = range(pageNumber - 1, max + 1).map((page, index) =>
    index < 3 || page === max ? _paginationItem(t, link, page, index, pageNumber === page, sortingValue) : false
  );

  const pages = _injectFirstPage(t, linksJsx, link, pageNumber, sortingValue);
  return concat([], slice(pages, 0, 5), _paginationSeparator(5428376546573873), slice(pages, 5));
}

/** < 1 ... 97 98 99 > */
export function splittedViewRight(t, max, link, pageNumber, sortingValue) {
  const linksJsx = range(max - 2, max + 1).map((page, index) =>
    _paginationItem(t, link, page, index, pageNumber === page, sortingValue)
  );

  return _injectFirstPage(t, linksJsx, link, pageNumber, sortingValue);
}

export function _linksJsx(t, link, pageNumber, max, sortingValue) {
  if (max <= 5) {
    return lessThen5ItemsView(t, max, link, pageNumber, sortingValue);
  } else {
    if (pageNumber < 3) {
      return splittedViewLeft(t, max, link, pageNumber, sortingValue);
    }

    if (pageNumber > max - 2) {
      return splittedViewRight(t, max, link, pageNumber, sortingValue);
    }

    return splittedViewMiddle(t, max, link, pageNumber, sortingValue);
  }
}

export const Pagination = ({ total, itemsPerPage, pageNumber, link, sortingValue }) => {
  const { t } = useTranslation();

  const pagePrev = _handlePage(total, itemsPerPage, pageNumber - 1);
  const pageNext = _handlePage(total, itemsPerPage, pageNumber + 1);
  const max = _maxNumberOfPages(total, itemsPerPage);

  return (
    total > 0 && (
      <nav aria-label={t('pagination.label')} className="ct-pagination">
        <ul className="pagination" data-e2e="pagination">
          <li className="page-item">
            <Link
              aria-label={t('pagination.previous.label')}
              className={pageNumber === 1 ? 'page-link muted' : 'page-link'}
              to={`${link.replace('#page#', pagePrev)}`}
              aria-disabled={pageNumber === 1}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </Link>
          </li>

          {_linksJsx(t, link, pageNumber, max, sortingValue)}

          <li className="page-item">
            <Link
              aria-label={t('pagination.next.label')}
              className={pageNumber === max ? 'page-link muted' : 'page-link'}
              to={`${link.replace('#page#', pageNext)}`}
              aria-disabled={pageNumber === max}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </Link>
          </li>
        </ul>
      </nav>
    )
  );
};

Pagination.propTypes = {
  link: PropTypes.string.isRequired,
};
